import { AnyAction, combineReducers } from 'redux';
import { success, error } from '@redux-requests/core';

import { BlankSafelist, Safelist, SafelistTerm } from 'shared/types/safelist';
import { IndexableObjectType } from 'shared/types/common';

import * as actions from './actions';
import { FetchSafelistsAction } from './types';

const safelistSummariesReducer = (state = {}, action: FetchSafelistsAction) => {
  // All actions will have a type, some will have a payload and request actions (ajax) have a response field
  const {type, response, meta} = action;

  switch (type) {
    case success(actions.FETCH_SAFELIST_SUMMARIES):
    case success(actions.MIGRATE_SAFELISTS):
      return response.reduce<IndexableObjectType>((safelistMap, crtSafelist) => {
        safelistMap[crtSafelist.guid] = crtSafelist;

        return safelistMap;
      }, {});
    case error(actions.FETCH_SAFELIST_SUMMARIES):
      return {};
    default:
      return state;
  }
};

const safelistToEditReducer = (state: Safelist | BlankSafelist = {}, action: AnyAction) => {
  const { type, response } = action;

  switch (type) {
    case success(actions.FETCH_SAFELIST_BY_GUID):
      return response;
    case success(actions.DELETE_SAFELIST_TERM):
      const { termGuid } = action.payload;

      return {
        ...state,
        entries: ((state as Safelist).entries || []).filter((entry) => entry.guid !== termGuid),
      };
    case actions.RESET_SAFELIST_EDIT_DATA:
      return {};
    default:
      return state;
  }
};

const extractedTermsReducer = (state = [], action: AnyAction) => {
  const { type, response, payload } = action;

  switch (type) {
    case actions.UPDATE_EXTRACTED_TERM:
      const {originalTerm, updatedTerm} = payload;

      // Find by guid if possible, otherwise compare entity (aka the value)
      const fieldToCompare = originalTerm.guid ? 'guid' : 'entity';

      // Find the term and update it in the array
      return state.map((term: SafelistTerm) => {
          // Update the term with only the fields that have been changed, and make sure its beingEdited flag is false
          return term[fieldToCompare] === originalTerm[fieldToCompare] ?
          Object.assign(term, updatedTerm, {isBeingEdited: false}) : term
      });

    case actions.ADD_MANUAL_EXTRACTED_TERM:
      // Adding a term manually sets the term to edit mode be default
      const newTerm = {...payload, isBeingEdited: true};
  
      return state.concat(newTerm);

    case actions.REMOVE_EXTRACTED_TERM:
      return state.filter((term: SafelistTerm) => term.entity !== payload.entity);
    case success(actions.EXTRACT_TERMS_FROM_TEXT):
      return response.data;
    case actions.CLEAR_EXTRACTED_TERMS:
      return [];
    case error(actions.EXTRACT_TERMS_FROM_TEXT):
    default:
      return state;
  }
};

const isAddingSafelistReducer = (state = false, action: AnyAction) => {
  const { type } = action;

  switch (type) {
    case actions.ADD_SAFELIST:
      return true;
    case success(actions.ADD_SAFELIST):
    case error(actions.ADD_SAFELIST):
      return false;
    default:
      return state;
  }
};

const isDeletingSafelistReducer = (state = false, action: AnyAction) => {
  const { type } = action;

  switch (type) {
    case actions.DELETE_SAFELIST:
      return true;
    case success(actions.DELETE_SAFELIST):
    case error(actions.DELETE_SAFELIST):
      return false;
    default:
      return state;
  }
};

const isDeletingSafelistTermReducer = (state = false, action: AnyAction) => {
  const { type } = action;

  switch (type) {
    case actions.DELETE_SAFELIST_TERM:
      return true;
    case success(actions.DELETE_SAFELIST_TERM):
    case error(actions.DELETE_SAFELIST_TERM):
      return false;
    default:
      return state;
  }
};

export default combineReducers({
  extractedTerms: extractedTermsReducer,
  isAddingSafelist: isAddingSafelistReducer,
  isDeletingSafelist: isDeletingSafelistReducer,
  isDeletingSafelistTerm: isDeletingSafelistTermReducer,
  safelistSummaries: safelistSummariesReducer,
  safelistToEdit: safelistToEditReducer,
});
